import clsx from "clsx";
import { Check, Plus } from "lucide-react";
import { useState } from "react";
import { Modal, useToast } from "@/components";
import Button from "@/components/ui/Button";
import MultiSelectDropdown from "@/components/ui/MultiSelectDropdown";
import { getDeviceModelLabel } from "@/utils/device-model";
import type { DeviceWithMapping } from "@/utils/mesh-device-utils";
import { getDeviceIcon } from "@/utils/mesh-device-utils";

interface SelectDeviceForLICModalProps {
  isOpen: boolean;
  onClose: () => void;
  licDevice: DeviceWithMapping | null;
  availableDevices: DeviceWithMapping[]; // All mesh devices (unmapped and mapped to other LICs)
  showStartDate?: boolean;
  onSelectDevices?: (
    items: Array<{
      meshDeviceId: string;
      licDeviceId: string;
      startDate: string;
    }>
  ) => void;
}

function SelectDeviceForLICModal({
  isOpen,
  onClose,
  licDevice,
  availableDevices,
  onSelectDevices,
  showStartDate = false,
}: SelectDeviceForLICModalProps) {
  const { showWarning } = useToast();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(new Date().toISOString());

  if (!licDevice || !licDevice.isLIC) {
    return null;
  }

  const handleSelectDevice = () => {
    if (selectedDeviceIds.length === 0) {
      showWarning({
        message: "Por favor, selecione pelo menos um dispositivo para adicionar à rede.",
      });
      return;
    }

    if (!startDate) {
      showWarning({
        message: "Por favor, selecione uma data de início.",
      });
      return;
    }

    if (onSelectDevices) {
      const startDateISO = new Date(startDate).toISOString();
      onSelectDevices(
        selectedDeviceIds.map((id) => ({
          meshDeviceId: id,
          licDeviceId: licDevice.id,
          startDate: startDateISO,
        }))
      );
      handleCancel();
    } else {
      showWarning({
        message: "Funcionalidade de adição ainda não implementada.",
      });
    }
  };

  const handleCancel = () => {
    setSelectedDeviceIds([]);
    setStartDate(new Date().toISOString());
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar Dispositivo à Rede" size="md">
      <div className="space-y-4">
        {/* LIC Device Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{getDeviceIcon(licDevice.device.model.id)}</span>
            <span className="font-medium text-gray-900">{licDevice.device.identifier}</span>
          </div>
          <p className="text-sm text-gray-600 mb-1">{getDeviceModelLabel(licDevice.device.model.id)}</p>
          <p className="text-sm text-green-600">
            Rede com {licDevice.meshDevices?.length || 0} dispositivo
            {(licDevice.meshDevices?.length || 0) !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Device Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Selecionar Dispositivos</label>

          {availableDevices.length === 0 ? (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center text-gray-500">
              <p>Nenhum dispositivo disponível</p>
              <p className="text-sm">Não há dispositivos mesh para mapear</p>
            </div>
          ) : (
            <MultiSelectDropdown
              items={availableDevices}
              selectedIds={selectedDeviceIds}
              onSelectedIdsChange={setSelectedDeviceIds}
              placeholder="Selecione dispositivos..."
              emptyMessage="Nenhum dispositivo disponível"
              renderItem={(device, isSelected) => (
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getDeviceIcon(device.device.model.id)}</span>
                  <div className="flex flex-col">
                    <span className="font-medium flex items-center gap-2">
                      {device.device.identifier}
                      {isSelected && <Check className="text-green-600" size={16} />}
                    </span>
                    <span
                      className={clsx("text-xs", {
                        "text-red-500": device.mappingStatus === "unmapped",
                        "text-green-500": device.mappingStatus === "mapped",
                      })}
                    >
                      {device.mappingStatus === "unmapped"
                        ? "Não mapeado"
                        : device.licDevice
                          ? `Mapeado para ${device.licDevice.device.identifier}`
                          : "Status desconhecido"}
                    </span>
                  </div>
                </div>
              )}
              renderSelectedItem={(device, itemCount) => (
                <>
                  <span className="text-lg">{getDeviceIcon(device.device.model.id)}</span>
                  <div className="flex flex-col">
                    <span className="font-medium text-left">
                      {itemCount === 1 ? device.device.identifier : `${device.device.identifier} +${itemCount - 1}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {itemCount === 1
                        ? device.mappingStatus === "unmapped"
                          ? "Não mapeado"
                          : device.licDevice
                            ? `Mapeado para ${device.licDevice.device.identifier}`
                            : "Status desconhecido"
                        : `${itemCount} dispositivos selecionados`}
                    </span>
                  </div>
                </>
              )}
            />
          )}
        </div>

        {/* Start Date */}
        {showStartDate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data de Início</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
        )}

        {/* Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Plus className="text-blue-600 mt-0.5" size={16} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Adicionando à rede mesh:</p>
              <p>
                O dispositivo selecionado será associado a este LIC e poderá receber comandos através dele. Se o
                dispositivo já estiver mapeado para outro LIC, ele será transferido para esta rede. A associação será
                ativa a partir da data especificada.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" className="flex-1" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleSelectDevice}
            disabled={selectedDeviceIds.length === 0 || availableDevices.length === 0}
          >
            Adicionar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SelectDeviceForLICModal;
